import {isCanonicalStoryMomentEntry} from './storyMomentEligibility';
import { getTimelinePlannerCardKey, getTimelineDedupKey } from '../../../services/timeline/timelineDeduping';
import { getCaptureFirstTimelineContext } from './captureFirstTimelineContext';
import { getTimelineEntryDate } from '../../../services/timeline/dateUtils';
import { resolveSourceStoryDisplayLabel } from './sourceStoryTitle';
import { getSourceFileName } from '../../../shared/sourceTitleResolver';
import {
  buildSourceCaptureReceipts,
  isEmailMessageWrapperEntry,
  isEmailSourceEntry,
  isCaptureDayReceiptEligibleEntry,
  getMedicationReviewSourceId,
  isSourceContainerReceiptEntry,
} from './sourceCaptureReceipt';
import { placeManualStoryOnTimelineDate } from './manualStoryPlacement';

export {isCanonicalStoryMomentEntry} from './storyMomentEligibility';

const normalizeText = (value = '') => String(value || '').trim();

const parseDateKey = (value = '') => {
  const dateKey = normalizeText(value);
  if (!dateKey) return null;
  const parsed = new Date(`${dateKey}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateKey = (date = null) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

const getLoggedAtDate = (entry = {}) => {
  const candidates = [
    entry.createdAt,
    entry.metadata?.createdAt,
    entry.source?.createdAt,
    entry.loggedAt,
    entry.metadata?.loggedAt,
  ];

  for (const candidate of candidates) {
    const date = candidate?.toDate?.() || (candidate ? new Date(candidate) : null);
    if (date instanceof Date && !Number.isNaN(date.getTime())) {
      return date;
    }
  }

  return null;
};

const formatLoggedDateLabel = (date = null) => (
  date instanceof Date && !Number.isNaN(date.getTime())
    ? `Logged ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    : ''
);

const getSourceNoteId = (entry = {}) => normalizeText(
  entry.sourceNoteId
  || entry.metadata?.sourceNoteId
  || entry.source?.sourceNoteId
  || ''
);

const getEntryTimestamp = (entry = {}) => {
  const date = getTimelineEntryDate(entry)
    || (typeof entry?.timestamp?.toDate === 'function' ? entry.timestamp.toDate() : null)
    || (entry.timestamp ? new Date(entry.timestamp) : null)
    || (entry.createdAt ? new Date(entry.createdAt) : null);

  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date(0);
};

const getStorySceneOccurredOnDate = (entry = {}) => {
  const storedDate = normalizeText(entry.occurredOnDate || entry.metadata?.timelinePlanner?.occurredOnDate || '');
  const resolvedTimelineDate = formatDateKey(getTimelineEntryDate(entry));
  const hasExplicitEmailDateLabel = isEmailSourceEntry(entry)
    && Boolean(normalizeText(entry.displayDate || entry.date || ''));
  const hasExplicitCalendarDay = Boolean(normalizeText(
    entry.entryDayKey
    || entry.localDate
    || entry.dateKey
    || entry.metadata?.timelinePlanner?.entryDayKey
    || entry.metadata?.timelinePlanner?.localDate
  ));

  if ((
    hasExplicitEmailDateLabel
    || (!storedDate && hasExplicitCalendarDay && isCaptureDayReceiptEligibleEntry(entry))
  ) && resolvedTimelineDate) {
    return resolvedTimelineDate;
  }

  return storedDate;
};

const getStorySceneTimestamp = (entry = {}) => parseDateKey(getStorySceneOccurredOnDate(entry))
  || getEntryTimestamp(entry);

const getStoryCaptureTimestamp = (entry = {}) => getLoggedAtDate(entry) || getEntryTimestamp(entry);

const getStoryCaptureDateKey = (entry = {}) => formatDateKey(getStoryCaptureTimestamp(entry));

const isCaptureDayStoryEligible = (story = {}) => (
  isCaptureDayReceiptEligibleEntry(story)
  || (story.sourceStoryScenes || []).some(isCaptureDayReceiptEligibleEntry)
);

const isStoryMomentReferenceEntry = (entry = {}) => entry.kind === 'story_moment_reference'
  || entry.pointerKind === 'story_moment_reference';

const buildStoryMomentReferenceItem = (scene = {}, storyItem = {}) => {
  const occurredOnDate = getStorySceneOccurredOnDate(scene);
  if (!occurredOnDate) {
    return null;
  }

  const storyCaptureDateKey = getStoryCaptureDateKey(storyItem);
  if (storyCaptureDateKey && occurredOnDate === storyCaptureDateKey) {
    return null;
  }

  const sourceNoteId = getSourceNoteId(scene) || getSourceNoteId(storyItem);
  const momentId = normalizeText(scene.id || scene.storyMomentId || scene.momentId || scene.plannerCardKey || '');
  if (!sourceNoteId || !momentId) {
    return null;
  }

  const momentTitle = normalizeText(scene.title || scene.titlePrefix || scene.momentTitle || scene.momentId || 'Moment');
  const storyTitle = normalizeText(storyItem.storyDisplayLabel || storyItem.sourceStoryLabel || 'Source story');
  const storyCaptureTimestamp = getStoryCaptureTimestamp(storyItem);
  const storyCaptureDateLabel = formatLoggedDateLabel(storyCaptureTimestamp);
  const storyCaptureShortLabel = normalizeText(storyCaptureDateLabel).replace(/^Logged\s+/i, '');
  const parentStoryLabel = storyTitle || 'Story';
  const exactMomentText = normalizeText(
    scene.metadata?.sourceSpanText
    || scene.sourceSpanText
    || scene.momentText
    || scene.momentBody
    || scene.storyMomentText
    || scene.sourceText
    || scene.text
    || scene.summary
    || scene.body
    || ''
  );
  const occurredDate = parseDateKey(occurredOnDate);
  const navigationTarget = `/sources/${sourceNoteId}?momentId=${encodeURIComponent(momentId)}`;
  const momentText = exactMomentText;

  return {
    id: `story-moment-reference:${sourceNoteId}:${momentId}:${occurredOnDate}`,
    kind: 'story_moment_reference',
    collection: 'dailyLogs',
    category: 'story_moment_reference',
    sourceStory: false,
    sourceId: sourceNoteId,
    sourceNoteId,
    storyId: sourceNoteId,
    storyMomentId: momentId,
    momentId,
    occurredOnDate,
    storyCaptureTimestamp,
    storyCaptureDateLabel,
    storyParentLabel: parentStoryLabel,
    timestamp: occurredDate || new Date(`${occurredOnDate}T12:00:00.000Z`),
    createdAt: occurredDate || new Date(`${occurredOnDate}T12:00:00.000Z`),
    loggedAt: occurredDate || new Date(`${occurredOnDate}T12:00:00.000Z`),
    title: momentTitle || parentStoryLabel || 'Moment',
    displayTitle: momentTitle || parentStoryLabel || 'Moment',
    body: momentText,
    summary: momentText,
    momentTitle,
    momentBody: momentText,
    bodyText: momentText,
    notesText: [momentTitle, occurredOnDate].filter(Boolean).join(' · '),
    primaryText: momentTitle,
    categoryLabel: 'Story moment',
    navigationTarget,
    storyCaptureShortLabel,
    visibility: scene.visibility || storyItem.visibility || '',
    parentStory: storyItem,
    authorName: scene.authorName || '',
    authorId: scene.authorId || '',
    createdBy: scene.createdBy || '',
    userId: scene.userId || '',
    caregiverName: scene.caregiverName || '',
    loggedByUser: scene.loggedByUser || '',
    loggedBy: scene.loggedBy || null,
    capturedBy: scene.capturedBy || null,
    authorRole: scene.authorRole || null,
    userRole: scene.userRole || null,
    loggedByRole: scene.loggedByRole || null,
    originalData: scene.originalData || null,
    pointerKey: `story-moment-reference:${sourceNoteId}:${momentId}:${occurredOnDate}`,
    candidateRefs: [],
  };
};

const isPlannerBackedSourceEntry = (entry = {}) => Boolean(
  getTimelinePlannerCardKey(entry)
  && getSourceNoteId(entry)
);

const getSourceContainerKind = (entry = {}) => normalizeText(
  entry.sourceContainerKind
  || entry.metadata?.timelinePlanner?.sourceContainerKind
  || entry.metadata?.timelinePlanner?.sourceContainer?.kind
  || ''
).toLowerCase();

const isEligibleForSourceStory = (entry = {}) => (
  isPlannerBackedSourceEntry(entry)
  && getSourceContainerKind(entry) === 'story'
);

const buildSourceStoryItem = (entries = []) => {
  const sortedScenes = [...entries].sort((left, right) => getStorySceneTimestamp(left) - getStorySceneTimestamp(right));
  const firstScene = sortedScenes[0] || {};
  const sourceContext = sortedScenes
    .map((entry) => getCaptureFirstTimelineContext(entry) || null)
    .find((context) => context?.hasVisibleContent)
    || getCaptureFirstTimelineContext(firstScene)
    || null;
  const sourceNoteId = getSourceNoteId(firstScene);
  const loggedAt = getStoryCaptureTimestamp(firstScene);
  const loggedDateLabel = formatLoggedDateLabel(loggedAt);
  const storyDisplayLabel = resolveSourceStoryDisplayLabel(firstScene, loggedDateLabel) || 'Source story';
  const sourceFileName = getSourceFileName(firstScene)
    || normalizeText(sourceContext?.sourceAttachments?.[0]?.fileName);
  const sourceStorySceneCount = sortedScenes.length;

  return {
    id: `source-story:${sourceNoteId}`,
    kind: 'source_story',
    collection: 'source_story',
    category: 'source_story',
    sourceStory: true,
    createdAt: loggedAt,
    loggedAt,
    storyDisplayLabel,
    sourceStoryLabel: storyDisplayLabel,
    sourceFileName,
    sourceStorySceneCount,
    sourceNoteId,
    sourceStoryScenes: sortedScenes,
    sourceStoryContext: sourceContext,
    timestamp: loggedAt,
    narrativeSortTime: loggedAt?.getTime?.() || 0,
    body: `${sourceStorySceneCount} moments from this source`,
  };
};

export const buildTimelinePresentationItems = (entries = []) => {
  const sourceEligibleById = new Map();

  entries.forEach((entry) => {
    if (isEmailMessageWrapperEntry(entry)) return;
    if (!isEligibleForSourceStory(entry)) return;
    const sourceNoteId = getSourceNoteId(entry);
    if (!sourceNoteId) return;
    if (!sourceEligibleById.has(sourceNoteId)) {
      sourceEligibleById.set(sourceNoteId, []);
    }
    sourceEligibleById.get(sourceNoteId).push(entry);
  });

  const sourceStoryIds = new Set(
    [...sourceEligibleById.entries()]
      .filter(([, sourceEntries]) => sourceEntries.length >= 1)
      .map(([sourceNoteId]) => sourceNoteId)
  );

  const directItems = [];
  const storyBuckets = new Map();

  entries.forEach((entry) => {
    if (isEmailMessageWrapperEntry(entry)) return;
    if (isEmailSourceEntry(entry) && isSourceContainerReceiptEntry(entry)) return;
    if (isStoryMomentReferenceEntry(entry)) {
      directItems.push({
        ...entry,
        sourceStory: false,
      });
      return;
    }

    const sourceNoteId = getSourceNoteId(entry);
    if (sourceNoteId && sourceStoryIds.has(sourceNoteId)) {
      if (!storyBuckets.has(sourceNoteId)) {
        storyBuckets.set(sourceNoteId, []);
      }
      storyBuckets.get(sourceNoteId).push(entry);
      return;
    }

    directItems.push({
      ...entry,
      kind: 'event_card',
      sourceStory: false,
    });
  });

  const storyBucketsWithReferences = [...storyBuckets.entries()].map(([sourceNoteId, sourceEntries]) => {
    const canonicalStoryEntries = sourceEntries.filter(isCanonicalStoryMomentEntry);
    const storyItem = buildSourceStoryItem(canonicalStoryEntries.length ? canonicalStoryEntries : sourceEntries);
    const uniqueReferences = new Map();

    (canonicalStoryEntries.length ? canonicalStoryEntries : sourceEntries).forEach((scene) => {
      const reference = buildStoryMomentReferenceItem(scene, storyItem);
      if (!reference) {
        return;
      }

      uniqueReferences.set(reference.id, reference);
    });

    return {
      sourceNoteId,
      storyItem,
      storyMomentReferences: [...uniqueReferences.values()],
    };
  });

  const storyItems = storyBucketsWithReferences.map((group) => group.storyItem);
  const storyMomentReferences = storyBucketsWithReferences.flatMap((group) => group.storyMomentReferences);

  return [...directItems, ...storyItems, ...storyMomentReferences]
    .sort((left, right) => getEntryTimestamp(left) - getEntryTimestamp(right));
};

const getEntryIdentity = (entry = {}, index = 0) => entry.id ? getTimelineDedupKey(entry) : normalizeText(
  entry.pointerKey
  || getTimelinePlannerCardKey(entry)
  || `${getSourceNoteId(entry)}:${index}`
);

export const buildTimelinePresentationItemsForDate = ({
  entries = [],
  calendarEntries = [],
  selectedDate = null,
} = {}) => {
  const selectedDateKey = formatDateKey(selectedDate);
  if (!selectedDateKey) {
    return buildTimelinePresentationItems(entries);
  }

  const allSourceReceipts = buildSourceCaptureReceipts(calendarEntries);
  const medicationReceiptSourceIds = new Set(allSourceReceipts
    .filter((item) => normalizeText(item.sourceReceiptTitle).startsWith('Medication document'))
    .map((item) => getSourceNoteId(item))
    .filter(Boolean));
  const captureDayStoryScenes = buildTimelinePresentationItems(calendarEntries)
    .filter((item) => (
      item.kind === 'source_story'
      && isCaptureDayStoryEligible(item)
      && getStoryCaptureDateKey(item) === selectedDateKey
      && !medicationReceiptSourceIds.has(getSourceNoteId(item))
    ))
    .flatMap((item) => item.sourceStoryScenes || []);
  const allReceiptSourceIds = new Set(allSourceReceipts.map((item) => getSourceNoteId(item)).filter(Boolean));
  const captureDaySourceReceipts = allSourceReceipts
    .filter((item) => formatDateKey(item.timestamp) === selectedDateKey);
  const receiptSourceIds = new Set(captureDaySourceReceipts.map((item) => getSourceNoteId(item)).filter(Boolean));
  const compositionEntries = new Map();
  const selectedCalendarEntries = calendarEntries.filter((entry) => {
    const status = normalizeText(entry?.status).toLowerCase();
    return !entry?.removedAt
      && !entry?.deletedAt
      && status !== 'deleted'
      && entry?.kind !== 'source_receipt'
      && entry?.kind !== 'source_story'
      && !entry?.isProfileUpdateReceipt
      && !isSourceContainerReceiptEntry(entry)
      && !allReceiptSourceIds.has(getMedicationReviewSourceId(entry))
      && formatDateKey(getTimelineEntryDate(entry)) === selectedDateKey;
  });

  [...entries, ...selectedCalendarEntries, ...captureDayStoryScenes].forEach((entry, index) => {
    if (
      medicationReceiptSourceIds.has(getSourceNoteId(entry))
    ) {
      return;
    }
    if (isEmailSourceEntry(entry) && isSourceContainerReceiptEntry(entry)) {
      return;
    }
    if (receiptSourceIds.has(getSourceNoteId(entry)) && isSourceContainerReceiptEntry(entry)) {
      return;
    }
    if (allReceiptSourceIds.has(getMedicationReviewSourceId(entry))) {
      return;
    }
    compositionEntries.set(getEntryIdentity(entry, index), entry);
  });

  return [
    ...buildTimelinePresentationItems([...compositionEntries.values()]),
    ...captureDaySourceReceipts,
  ]
    .map((item) => placeManualStoryOnTimelineDate(item, selectedDateKey))
    .filter((item) => formatDateKey(getTimelineEntryDate(item)) === selectedDateKey);
};

export const getTimelinePresentationDiagnostics = (entries = [], presentationItems = []) => {
  const sources = new Map();

  entries.forEach((entry) => {
    const sourceNoteId = getSourceNoteId(entry);
    const sourceKey = sourceNoteId || '(missing sourceNoteId)';
    if (!sources.has(sourceKey)) {
      sources.set(sourceKey, {
        sourceNoteId,
        entryCount: 0,
        plannerCount: 0,
        storyMarkedCount: 0,
        entries: [],
      });
    }

    const source = sources.get(sourceKey);
    const plannerCardKey = getTimelinePlannerCardKey(entry);
    const sourceContainerKind = getSourceContainerKind(entry);
    source.entryCount += 1;
    if (plannerCardKey) source.plannerCount += 1;
    if (sourceContainerKind === 'story') source.storyMarkedCount += 1;
    source.entries.push({
      id: entry.id || '',
      title: normalizeText(entry.title || entry.titlePrefix || entry.category || 'Untitled'),
      plannerCardKey,
      sourceContainerKind: sourceContainerKind || '(missing)',
      alternateSourceId: normalizeText(
        entry.careSourceId
        || entry.sourceId
        || entry.metadata?.careSourceId
        || entry.metadata?.sourceId
        || ''
      ),
    });
  });

  return {
    compositorCalled: true,
    inputCount: entries.length,
    storyCount: presentationItems.filter((item) => item.kind === 'source_story').length,
    standaloneCount: presentationItems.filter((item) => item.kind !== 'source_story').length,
    sources: [...sources.values()].map((source) => {
      let outcome = 'standalone: sourceNoteId missing';
      if (source.sourceNoteId && source.plannerCount < source.entryCount) {
        outcome = 'standalone: plannerCardKey missing on one or more records';
      } else if (source.sourceNoteId && source.storyMarkedCount === 0) {
        outcome = 'standalone: no records marked story';
      } else if (source.sourceNoteId && source.storyMarkedCount < source.entryCount) {
        outcome = 'grouped as Story (data-fetch warning: fewer records present than resolver marked story)';
      } else if (source.sourceNoteId) {
        outcome = 'grouped as Story';
      }
      return { ...source, outcome };
    }),
  };
};

export default buildTimelinePresentationItems;
